import { memo, useMemo, useState } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type Clip, type ScopedPermissions, type ShortUrlResponse } from '@air/api/types';
import { type TabPanel } from '@air/primitive-tabs';

import { SharePrivateClipLinkInfo } from '~/components/SharePrivateClipModal/SharePrivateClipLinkInfo';
import { SharePrivateClipSettings } from '~/components/SharePrivateClipModal/SharePrivateClipSettings';
import { SharePrivateContentTabs } from '~/components/SharePrivateContentModal/SharePrivateContentTabs';

type SharePrivateClipTabsProps = {
  clip: Pick<Clip, 'id' | 'assetVersionCount' | 'ext'>;
  trackLocation: AirActionTrackingLocation;
  boardContext: string | undefined;
  shortIdInfo: ShortUrlResponse;
  userPermissions: ScopedPermissions;
};

export const SharePrivateClipTabs = memo(
  ({ clip, shortIdInfo, trackLocation, boardContext, userPermissions }: SharePrivateClipTabsProps) => {
    const [activeTab, setActiveTab] = useState('link');

    const panels = useMemo<TabPanel[]>(() => {
      return [
        {
          id: 'link',
          label: 'Link',
          content: (
            <SharePrivateClipLinkInfo
              shortIdInfo={shortIdInfo}
              clip={clip}
              trackLocation={trackLocation}
              goToSettings={() => setActiveTab('settings')}
              boardContext={boardContext}
              userPermissions={userPermissions}
            />
          ),
        },
        {
          id: 'settings',
          label: 'Settings',
          content: (
            <SharePrivateClipSettings clipId={clip.id} shortIdInfo={shortIdInfo} userPermissions={userPermissions} />
          ),
        },
      ];
    }, [boardContext, clip, shortIdInfo, trackLocation, userPermissions]);

    return <SharePrivateContentTabs setActiveTab={setActiveTab} activeTab={activeTab} panels={panels} />;
  },
);

SharePrivateClipTabs.displayName = 'SharePrivateClipTabs';
